import { Component } from "@grafikr/components";
import Drawer from "utils/components/drawer";

export default Component((node, { app, onMounted, dispatchEvent }) => {
  const sectionId = node.dataset.sectionId;
  let debounceTimer: number | null = null;

  // Sort
  const initSort = () => {
    const sortContainer = node.querySelector("[data-sort-container]");
    const sortButton = sortContainer.querySelector("[data-sort-button]");
    const sortDropdown = sortContainer.querySelector("[data-sort-dropdown]");
    const sortForm = sortContainer.querySelector("[data-sort-form]");
    const sortSelect = sortForm.querySelector("select");
    const sortLabel = sortContainer.querySelector("[data-sort-label]");

    const isMobile = window.innerWidth < 1024;

    const sortDrawerElement = document.getElementById("collection-sort-drawer");
    const sortDrawer = new Drawer(sortDrawerElement);

    const activeOptionClass = "collection__sort__dropdown__option--active";
    const activeDropdownClass = "collection__sort__dropdown--active";

    sortDrawer.on("open", () => (document.body.style.overflow = "hidden"));
    sortDrawer.on("close", () => (document.body.style.overflow = "auto"));

    const handleSortOptionClick = (option) => {
      const selectedValue = option.dataset.sortOption;
      sortSelect.value = selectedValue;
      sortLabel.textContent = option.textContent.trim();

      const optionsContainer = isMobile ? sortDrawerElement : sortDropdown;
      const activeOptions = optionsContainer.querySelectorAll(`[data-sort-option].${activeOptionClass}`);
      activeOptions.forEach((el) => el.classList.remove(activeOptionClass));
      option.classList.add(activeOptionClass);

      if (isMobile) {
        sortDrawer.close();
      } else {
        sortDropdown.classList.remove(activeDropdownClass);
      }

      if (sortForm instanceof HTMLFormElement) {
        sortForm.dispatchEvent(new Event("change", { bubbles: true }));
      } else {
        console.error("Form element is not an HTMLFormElement");
      }
    };

    sortButton.addEventListener("click", () => {
      if (isMobile) {
        sortDrawer.open();
      } else {
        sortDropdown.classList.toggle(activeDropdownClass);
      }
    });

    const sortOptions = isMobile
      ? sortDrawerElement.querySelectorAll("[data-sort-option]")
      : sortDropdown.querySelectorAll("[data-sort-option]");

    sortOptions.forEach((option) => {
      option.addEventListener("click", () => handleSortOptionClick(option));
    });

    if (!isMobile) {
      document.addEventListener("click", (event) => {
        const clickedOutside = !sortContainer.contains(event.target as Node);
        if (clickedOutside) {
          sortDropdown.classList.remove(activeDropdownClass);
        }
      });
    }
  };

  // Filter
  const initFilterDrawer = () => {
    const filterDrawerElement = document.getElementById("collection-filter-drawer");
    const filterDrawer = new Drawer(filterDrawerElement);

    filterDrawer.on("open", () => (document.body.style.overflow = "hidden"));
    filterDrawer.on("close", () => (document.body.style.overflow = "auto"));

    const mainFilterButton = node.querySelector<HTMLElement>("[data-filter-button]");
    mainFilterButton.addEventListener("click", () => filterDrawer.open());

    const drawerCloseButtons = filterDrawerElement.querySelectorAll("[data-close]");
    drawerCloseButtons.forEach((button) => {
      button.addEventListener("click", () => filterDrawer.close());
    });

    const stickyFilterButton = document.querySelector<HTMLElement>("[data-filter-button-sticky]");
    stickyFilterButton.addEventListener("click", () => filterDrawer.open());

    initStickyFilter(stickyFilterButton);
  };

  const initStickyFilter = (stickyFilterButton: HTMLElement) => {
    const updateStickyFilterButtonVisibility = () => {
      const mainButton = document.querySelector("[data-filter-button]");
      const productGrid = document.querySelector("[data-grid]");

      if (!mainButton || !productGrid) return;

      const mainButtonRect = mainButton.getBoundingClientRect();
      const gridRect = productGrid.getBoundingClientRect();

      const isMainButtonVisible = mainButtonRect.top >= 0 && mainButtonRect.bottom <= window.innerHeight;
      const isGridVisible = gridRect.top < window.innerHeight && gridRect.bottom >= 0;

      if (isMainButtonVisible || !isGridVisible) {
        stickyFilterButton.classList.remove("filter__button__sticky--active");
      } else {
        stickyFilterButton.classList.add("filter__button__sticky--active");
      }
    };

    document.addEventListener("scroll", updateStickyFilterButtonVisibility, { passive: true });

    updateStickyFilterButtonVisibility();

    return () => {
      document.removeEventListener("scroll", updateStickyFilterButtonVisibility);
    };
  };

  const initImageDisplay = () => {
    const collectionSection = document.querySelector("[data-collection-section]");
    const preferredView = document.querySelector<HTMLInputElement>("[data-preferred-view]").dataset.preferredView;
    const imageDisplayInputs = document.querySelectorAll<HTMLInputElement>('input[name="image-display"]');
    const STORAGE_KEY = "preferred_image_display";

    const showModelImage = () => {
      collectionSection.classList.remove("offset__images");
      collectionSection.classList.add("preference__set");
    }

    const showProductImage = () => {
      collectionSection.classList.add("offset__images");
      collectionSection.classList.add("preference__set");
    }

    const showDefaultImage = () => {
      collectionSection.classList.remove("offset__images");
      collectionSection.classList.remove("preference__set");
    }

    const updateGridClass = () => {
      const modelInput = document.getElementById("model-first") as HTMLInputElement;
      const productInput = document.getElementById("product-first") as HTMLInputElement;
      if (modelInput.checked) {
        showModelImage();
      } else if(productInput.checked) {
        showProductImage();
      } else {
        showDefaultImage();
      }
    };

    const savePreference = (inputId: string) => {
      sessionStorage.setItem(STORAGE_KEY, inputId);
    };

    const savedPreference = sessionStorage.getItem(STORAGE_KEY);
    if (preferredView != "") {
      switch (preferredView) {
        case "Model":
          showModelImage();
          const modelInput = document.getElementById("model-first") as HTMLInputElement;
          modelInput.checked = true;
          break;
        case "Packshot":
          showProductImage();
          const productInput = document.getElementById("product-first") as HTMLInputElement;
          productInput.checked = true;
          break;
      }
    } else if (savedPreference) {
      const savedInput = document.getElementById(savedPreference) as HTMLInputElement;

      switch (savedPreference) {
        case "model-first":
          showModelImage();
          break;
        case "product-first":
          showProductImage();
          break;
        case "default":
          showDefaultImage();
          break;
      }

      if (savedInput) {
        savedInput.checked = true;
      }
    }

    imageDisplayInputs.forEach((input) => {
      input.addEventListener("change", () => {
        if (input.checked) {
          savePreference(input.id);
        } else {
          savePreference('default')
        }
        imageDisplayInputs.forEach((otherInput) => {
          if (otherInput !== input) {
            otherInput.checked = false;
          }
        });
        updateGridClass();
      });
    });
  };

  const getUrl = (url?: string) => {
    if (url) {
      const base = new URL(url, window.location.origin);
      base.searchParams.set("section_id", sectionId);

      return base;
    } else {
      const base = new URL(window.location.href, window.location.origin);
      const params = new URLSearchParams({
        section_id: sectionId,
      });

      const filterForm = document.querySelector("[data-collection-filter-form]");
      const sortForm = document.querySelector("[data-sort-form]");

      const filterFormData = new FormData(filterForm as HTMLFormElement);
      const sortFormData = new FormData(sortForm as HTMLFormElement);

      filterFormData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      sortFormData.forEach((value, key) => {
        params.append(key, value.toString());
      });

      base.search = params.toString();

      return base;
    }
  };

  const getOutput = async (url?: string) => {
    const response = await fetch(getUrl(url));
    const section = await response.text();

    return section;
  };

  const updateUrl = (url?: string) => {
    const base = getUrl(url);

    const params = new URLSearchParams(base.search);
    params.delete("section_id");

    base.search = params.toString();

    window.history.replaceState({}, "", base.toString());
  };

  const renderSection = async (url?: string) => {
    try {
      const section = await getOutput(url);
      const html = new DOMParser().parseFromString(section, "text/html");
      const grid = html.querySelector<HTMLElement>("[data-grid]");
      const resultsCount = html.querySelector<HTMLElement>("[data-results-count]");
      const pagination = html.querySelector<HTMLElement>("[data-pagination]");
      const filterContents = html.querySelectorAll<HTMLElement>("[data-filter-content]");
      const filterOutput = html.querySelector<HTMLElement>("[data-filter-output]");
      const resultsButton = html.querySelector<HTMLElement>("[data-results-button]");

      if (grid) {
        const previousGrid = document.querySelector<HTMLElement>("[data-grid]");
        previousGrid.classList.add("animate-pulse");
        previousGrid.replaceWith(grid);
      }

      if (resultsCount) {
        const previousResultsCount = document.querySelector<HTMLElement>("[data-results-count]");
        previousResultsCount.replaceWith(resultsCount);
      }

      if (pagination) {
        const previousPagination = document.querySelector<HTMLElement>("[data-pagination]");
        previousPagination.replaceWith(pagination);
      }

      if (filterContents) {
        filterContents.forEach((content) => {
          const previousContent = document.querySelector<HTMLElement>(
            `[data-filter-content="${content.dataset.filterContent}"]`
          );

          previousContent.innerHTML = content.innerHTML;
        });
      }

      if (filterOutput) {
        const previousFilterOutput = document.querySelector<HTMLElement>("[data-filter-output]");
        previousFilterOutput.replaceWith(filterOutput);
      }

      if (resultsButton) {
        const previousResultsButton = document.querySelector<HTMLElement>("[data-results-button]");
        previousResultsButton.innerHTML = resultsButton.innerHTML;
      }

      updateUrl(url);
      app.mount();

      dispatchEvent("filter:update", false);
    } catch (error) {
      console.error("Failed to render section:", error);
    }
  };

  const debounce = (func: Function, delay: number) => {
    return (...args: any[]) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        func.apply(null, args);
      }, delay) as unknown as number;
    };
  };

  const changeHandler = debounce(async () => {
    await renderSection();
  }, 1000);

  const submitHandler = async (event: Event) => {
    event.preventDefault();
    await renderSection();

    dispatchEvent("filter:submit", false);
  };

  const urlHandler = async (event: Event) => {
    if (!(event.target instanceof HTMLAnchorElement)) {
      return;
    }

    if (!event.target.hasAttribute("data-filter-change")) {
      return;
    }

    event.preventDefault();
    await renderSection(event.target.href);
  };

  document.addEventListener("click", urlHandler);

  const showLoadingState = () => {
    const previousGrid = document.querySelector<HTMLElement>("[data-grid]");
    previousGrid.classList.add("animate-pulse");
    const resultsButton = document.querySelector<HTMLElement>("[data-results-button]");
    const resultsButtonText = resultsButton.querySelector("span");
    const resultsButtonLoader = resultsButton.querySelector("svg");

    resultsButtonText.style.display = "none";
    resultsButtonLoader.style.display = "inline-block";
  };

  onMounted(() => {
    initSort();
    initFilterDrawer();
    initImageDisplay();

    node.addEventListener("change", () => {
      showLoadingState();
      changeHandler();
    });
    node.addEventListener("submit", submitHandler);

    document.addEventListener("change", (event) => {
      const target = event.target as HTMLElement;
      if (target instanceof HTMLInputElement && target.dataset.formId === node.id) {
        node.dispatchEvent(new Event("change"));
      }
    });
  });
});
